import { toast } from 'sonner'
import { usersApi } from '@/api/users'
import type { UserListItem } from '@/api/users'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Loader2 } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { showError } from '@/lib/error'

interface UserStatusConfirmDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	user: UserListItem | null
}

export function UserStatusConfirmDialog({ open, onOpenChange, user }: UserStatusConfirmDialogProps) {
	const queryClient = useQueryClient()
	const newStatus = user?.status === 1 ? 0 : 1
	const action = newStatus === 0 ? '禁用' : '启用'

	const statusMutation = useMutation({
		mutationFn: ({ id, status }: { id: number; status: number }) =>
			usersApi.updateStatus(id, status),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['users'] })
		},
	})

	const handleConfirm = async () => {
		if (!user) return
		try {
			await statusMutation.mutateAsync({ id: user.id, status: newStatus })
			toast.success(`已${action}用户 ${user.nickname || user.username}`)
			onOpenChange(false)
		} catch (error) {
			console.error(`${action}用户失败:`, error)
			showError(error, `${action}用户失败`)
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[400px]">
				<DialogHeader>
					<DialogTitle>确认{action}用户</DialogTitle>
					<DialogDescription>
						确定要{action}用户「{user?.nickname || user?.username}」吗？
						{newStatus === 0 && '禁用后该用户将无法登录系统。'}
					</DialogDescription>
				</DialogHeader>
				<DialogFooter className="gap-2 sm:gap-0">
					<Button
						type="button"
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={statusMutation.isPending}
					>
						取消
					</Button>
					<Button
						type="button"
						variant={newStatus === 0 ? 'destructive' : 'default'}
						onClick={handleConfirm}
						disabled={statusMutation.isPending}
					>
						{statusMutation.isPending && (
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						)}
						确认{action}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
